import { useMutation } from '@apollo/react-hooks';
import { Button, Grid, Stack, TextField, Typography } from '@mui/material';
import React, { ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { CREATE_INVOICE_PAYMENT_REQUEST } from '../../../gql/subscriptions';
import { Channel, InvoicePaymentRequestInput } from '../../../shared/types';
import TermsAndConditionsCheckbox from '../../ui/TermsAndConditionsCheckbox';
import { useFormControl, useFormGroup } from '../../../utils/form.utils';
import {
    booleanTrueValidator,
    requiredValidator,
    validateEmail,
} from '../../../utils/validator.utils';

interface PremiumChannelInvoicePaymentProps {
    channelId: string;
    successUrl: string;
    goBack: () => void;
}

export const PremiumChannelInvoicePayment = (
    props: PremiumChannelInvoicePaymentProps
) => {
    const { channelId, successUrl, goBack } = props;

    const form = useFormGroup({
        legalEntityName: useFormControl<string>('', [requiredValidator]),
        billingEmailAddress: useFormControl<string>('', [
            requiredValidator,
            validateEmail,
        ]),
        acceptedTerms: useFormControl<boolean>(false, [booleanTrueValidator]),
    });

    const history = useHistory();

    const [createInvoiceChannelSubscription] = useMutation<
        Channel,
        { item: InvoicePaymentRequestInput }
    >(CREATE_INVOICE_PAYMENT_REQUEST, {
        onCompleted: () => {
            history.replace(successUrl);
        },
    });

    const onSubmit = async (ev: any) => {
        if (!channelId) return;

        ev?.preventDefault();
        form.setDirty();
        if (!form.isValid) {
            return;
        }

        return createInvoiceChannelSubscription({
            variables: {
                item: {
                    channelId,
                    legalEntityName: form.value.legalEntityName,
                    billingEmailAddress: form.value.billingEmailAddress,
                },
            },
        });
    };

    return (
        <Grid container spacing={4}>
            <Grid size={12}>
                <Stack spacing={2}>
                    <Typography variant="body1">
                        A member of the Neon Concierge team will get in touch.
                    </Typography>
                    <TextField
                        id={'legalEntityName-input'}
                        fullWidth
                        error={form.legalEntityName.showError}
                        helperText={
                            form.legalEntityName.showError
                                ? form.legalEntityName.validationMessage
                                : undefined
                        }
                        type="text"
                        label="Legal entity name"
                        autoComplete="off"
                        value={form.legalEntityName.value}
                        onBlur={form.legalEntityName?.handleBlur}
                        onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                            const val: string = e?.target?.value;
                            form.legalEntityName?.setValue(val);
                        }}
                    />
                    <TextField
                        id={'billingEmailAddress-input'}
                        fullWidth
                        error={form.billingEmailAddress.showError}
                        helperText={
                            form.billingEmailAddress.showError
                                ? form.billingEmailAddress.validationMessage
                                : undefined
                        }
                        type="text"
                        label="Billing email address"
                        autoComplete="off"
                        value={form.billingEmailAddress.value}
                        onBlur={form.billingEmailAddress.handleBlur}
                        onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                            const val: string = e?.target?.value;
                            form.billingEmailAddress.setValue(val);
                        }}
                    />
                </Stack>
            </Grid>
            <Grid size={12}>
                <TermsAndConditionsCheckbox
                    acceptedTerms={form.acceptedTerms.value}
                    setAcceptedTerms={(acceptedTerms: boolean) =>
                        form.acceptedTerms.setValue(acceptedTerms)
                    }
                    showError={form.acceptedTerms.showError}
                />
            </Grid>
            <Grid size={12}>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={goBack}
                    >
                        Back
                    </Button>
                    <Button variant="contained" onClick={onSubmit}>
                        Send invoice request
                    </Button>
                </Stack>
            </Grid>
        </Grid>
    );
};
